import * as THREE from "three/webgpu";
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {
    SketchBezierCurveClosureKind,
    SketchBezierCurveKind,
    SketchBezierSelectionKind,
    SketchLineObject,
    SketchPointObject,
} from '../mesh/sketch';
import {SketchStyleSplineObject} from '../mesh/sketch/SketchStyleSplineObject';
import {isDashDotLIneNodeMaterial, LineMaterialFactory} from '../material';
import {
    CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS,
    type CurveSampleSegmentOptions,
    resolveCurveSampleSegments,
    SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS,
    SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS,
} from '../tool/resolveCurveSampleSegments';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';
import {scheduleDisposableResourceDisposal} from '../utils';

/** 样条预览颜色。 */
const SPLINE_PREVIEW_COLOR = 0x53a9ff;

/**
 * 样式样条线模式枚举。
 */
export enum StyleSplineMode {
    /** 贝塞尔。 */
    Bezier = 'bezier',
    /** 三次B样条。 */
    BSpline3 = 'bspline-3',
    /** 五次B样条。 */
    BSpline5 = 'bspline-5',
    /** 七次B样条。 */
    BSpline7 = 'bspline-7',
}

/**
 * 风格样条线绘制选项。
 */
export interface StyleSplineOptions {
    /** 样条线模式。 */
    mode: StyleSplineMode;
    /** 是否为构造几何。 */
    forConstruction: boolean;
    /** 是否显示曲率。 */
    showCurvature: boolean;
    /** 是否保持内部连续性。 */
    keepInternalContinuity: boolean;
}

/**
 * 草图样式样条曲线绘制处理器。
 * 职责：
 * 1. 仅负责样式样条曲线绘制与预览；
 * 2. 独立维护样式样条选项、控制点预览与实体创建；
 * 3. 不再继承普通样条绘制处理器，避免职责耦合。
 */
export class SketchStyleSplineDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.StyleSpline;
    /** 当前样式样条绘制流程中“已点击确认”的控制点预览对象列表。 */
    private readonly previewControlPointObjects: SketchPointObject[] = [];
    /** 当前样式样条绘制流程中跟随鼠标移动的临时控制点预览对象。 */
    private previewMovingControlPointObject: SketchPointObject | null = null;
    /** 当前是否正在执行样式样条完成流程。 */
    private isFinishingMultiPointDrawing: boolean = false;
    /** 每次样式样条绘制点击命中的捕捉目标点对象列表（与 drawingPoints 同步），用于实体化控制点时复用，确保选择链拓扑完整。 */
    private drawingPointTargets: Array<SketchPointObject | null> = [];
    /** 当前等待执行的样式样条预览请求。 */
    private pendingStyleSplinePreviewRequest: {
        /** 当前鼠标预览点。 */
        currentPoint: THREE.Vector3;
        /** 当前预览版本号。 */
        currentVersion: number;
    } | null = null;
    /** 是否正在等待 Worker 返回样式样条预览结果。 */
    private isStyleSplinePreviewRequestRunning = false;
    /** 样式样条工具选项。 */
    public styleSplineOptions: StyleSplineOptions = {
        mode: StyleSplineMode.Bezier,
        forConstruction: false,
        showCurvature: false,
        keepInternalContinuity: false,
    };

    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    public constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    
    getRequiredPointCount(): number {
        return Infinity;
    }

    
    get closable(): boolean {
        return true;
    }

    
    getDrawingHint(): string {
        let baseHint = '依次点击控制点绘制样式样条曲线';
        if (this.styleSplineOptions.mode === StyleSplineMode.Bezier) {
            baseHint = '依次点击控制点，曲线仅穿过首尾点';
        } else if (this.styleSplineOptions.mode === StyleSplineMode.BSpline3) {
            baseHint = '依次点击控制点，B-样条（度数3）';
        } else if (this.styleSplineOptions.mode === StyleSplineMode.BSpline5) {
            baseHint = '依次点击控制点，B-样条（度数5）';
        } else if (this.styleSplineOptions.mode === StyleSplineMode.BSpline7) {
            baseHint = '依次点击控制点，B-样条（度数7）';
        }
        const pointCount = this.drawingPoints.length;
        const extraInfo = pointCount > 0 ? ` (${pointCount}个点)` : '';
        const actionHint = pointCount >= 2
            ? '双击/Enter完成 | Backspace撤销点 | ESC取消'
            : 'ESC取消';
        return `${baseHint}${extraInfo} | ${actionHint}`;
    }

    
    resetState(): void {
        this.drawingPoints = [];
        this.drawingPointTargets = [];
        this.isFinishingMultiPointDrawing = false;
        this.pendingStyleSplinePreviewRequest = null;
        this.destroyStyleSplinePreviewCompletely();
    }

    /**
     * 处理样式样条绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。
     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        const resolved = this.resolveMultiPointDrawingPoint(payload);
        if (!resolved) {
            console.log('无法获取样式样条绘制位置');
            return;
        }
        // 闭合/双击完成时提前返回，不追加绘制点，捕捉目标也不记录。
        if (this.handleMultiPointCommit(payload)) {
            return;
        }
        this.drawingPoints.push(resolved.clone());
        // 逐点记录捕捉目标端点，实体化控制点时逐点复用，确保选择链拓扑完整。
        this.drawingPointTargets.push(payload.snappedTargetPoint ?? null);
    }

    /**
     * 撤销样式样条绘制命令内尚未提交的最后一个输入点。
     * 说明：捕捉目标列表与绘制点一一对应，撤销点级输入时必须同步截断，避免残留引用错位。
     * @returns 是否真实撤销了一个未提交输入点。
     */
    public override undoLastDrawingPoint(): boolean {
        if (this.drawingPoints.length === 0) {
            return false;
        }
        this.drawingPoints.pop();
        this.drawingPointTargets.pop();
        return true;
    }

    /**
     * 更新样式样条预览。
     * @param currentPoint 当前预览点。
     * @param currentVersion 当前预览版本号。
     */
    async updatePreview(
        currentPoint: THREE.Vector3,
        currentVersion: number,
    ): Promise<void> {
        if (this.isFinishingMultiPointDrawing) {
            return;
        }
        this.pendingStyleSplinePreviewRequest = {
            currentPoint: currentPoint.clone(),
            currentVersion,
        };
        if (this.isStyleSplinePreviewRequestRunning) {
            return;
        }

        this.isStyleSplinePreviewRequestRunning = true;
        try {
            while (this.pendingStyleSplinePreviewRequest) {
                const request = this.pendingStyleSplinePreviewRequest;
                this.pendingStyleSplinePreviewRequest = null;
                await this.updateStyleSplinePreviewByHandler(request.currentPoint, request.currentVersion);
            }
        } finally {
            this.isStyleSplinePreviewRequestRunning = false;
        }
    }

    /**
     * 更新样式样条预览。
     * @param currentPoint 当前预览点。
     * @param currentVersion 当前预览版本号。
     */
    async updateStyleSplinePreviewByHandler(
        currentPoint: THREE.Vector3,
        currentVersion: number,
    ): Promise<void> {
        if (this.drawingPoints.length === 0) {
            super.destroyPreview();
            return;
        }
        const controlPoints = [...this.drawingPoints, currentPoint.clone()];
        const mode = this.styleSplineOptions.mode;
        const degree = mode === StyleSplineMode.BSpline3 ? 3
            : mode === StyleSplineMode.BSpline5 ? 5
            : mode === StyleSplineMode.BSpline7 ? 7
            : undefined;
        const curvePoints = await this.computeWorkerCurvePoints(
            mode === StyleSplineMode.Bezier ? '2d_bezier' : '2d_bspline',
            controlPoints,
            degree,
            SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS,
        );
        if (currentVersion !== this.sketchDrawingManager.previewVersion) {
            return;
        }
        //------ 预览自相交冻结 开始------
        // 预览曲线自相交时整块冻结预览：曲线预览线、辅助控制折线、控制点预览对象全部保留
        // 上一次合法状态，直到鼠标移出自交区域恢复跟随。
        // 样式样条预览路径没有独立闭合判定，这里以采样点列首尾是否重合为准，
        // 闭合预览的接缝边对因此在检测中豁免。
        // 检测必须在基准面局部 2D 内进行：直接把世界坐标喂给检测等价于投影到世界 XY 平面，
        // 会让非 XY 平行的基准面（用户创建基准面、上视、右视等）在回画时误判自交并冻结预览。
        const isClosedPreview = curvePoints.length >= 2
            && this.isClosedCurve(curvePoints[0]!, curvePoints[curvePoints.length - 1]!);
        const isSelfIntersectingPreview = this.isPolylineSelfIntersectingInPlane(curvePoints, {
            closed: isClosedPreview,
        });
        if (isSelfIntersectingPreview) {
            return;
        }
        //------ 预览自相交冻结 结束------
        this.renderCurvePreviewInHandler({
            curvePoints,
            controlPoints,
            isConstruction: this.styleSplineOptions.forConstruction,
            showControlPolyline: true,
        });
    }

    /**
     * 请求 Worker 计算曲线采样点。
     * @param curveType 曲线类型。
     * @param controlPoints 控制点列表。
     * @param degree B 样条阶数。
     * @returns 曲线点列表。
     */
    private async computeWorkerCurvePoints(
        curveType: '2d_bezier' | '2d_bspline',
        controlPoints: THREE.Vector3[],
        degree?: number,
        sampleOptions?: CurveSampleSegmentOptions,
    ): Promise<THREE.Vector3[]> {
        const segments = resolveCurveSampleSegments(controlPoints, sampleOptions);
        const result = await this.viewer.workerManager.compute2DCurve({
            curveType,
            params: {
                points: controlPoints.map((point) => ({x: point.x, y: point.y, z: point.z})),
                segments,
                degree,
            },
        });
        return result.points.map((point) => new THREE.Vector3(point.x, point.y, point.z));
    }

    /**
     * 在处理器内渲染曲线预览。
     * @param options 预览渲染选项。
     */
    private renderCurvePreviewInHandler(options: {
        curvePoints: THREE.Vector3[];
        controlPoints: THREE.Vector3[];
        isConstruction: boolean;
        showControlPolyline: boolean;
    }): void {
        if (options.curvePoints.length < 2) {
            super.destroyPreview();
            return;
        }

        if (options.showControlPolyline) {
            this.renderControlPolylinePreviewSegments(options.controlPoints);
        }


        this.syncCurvePreviewLine(options.curvePoints, options.isConstruction);
        this.syncPreviewControlPointObjects(options.controlPoints);
        // 此工具会在一个异步调用内消费多轮合并请求，每轮实际写入后都需通知重绘。
        // 不能只等最外层 updatePreview 返回，否则持续输入时已更新的曲线仍停留在旧画面。
        this.viewer.requestRender();
    }

    /**
     * 同步样式样条曲线预览线。
     *
     * @param curvePoints 曲线采样点列表。
     * @param isConstruction 是否按构造线样式显示。
     */
    private syncCurvePreviewLine(curvePoints: THREE.Vector3[], isConstruction: boolean): void {
        const existingLine = this.sketchDrawingManager.previewExtraLine;
        if (existingLine && this.canReuseCurvePreviewLine(existingLine, isConstruction)) {
            this.replacePreviewLineGeometry(existingLine, curvePoints);
            if (existingLine.parent !== this.viewer.scene) {
                this.viewer.addObject(existingLine);
            }
            return;
        }

        if (existingLine) {
            this.disposePreviewLineObject(existingLine);
        }

        const curveLine = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(curvePoints),
            this.createCurvePreviewMaterial(isConstruction),
        );
        if (curveLine.material instanceof THREE.LineDashedMaterial) {
            curveLine.computeLineDistances();
        }
        this.sketchDrawingManager.previewExtraLine = curveLine;
        this.viewer.addObject(curveLine);
    }

    /**
     * 判断现有曲线预览线是否可以复用。
     *
     * @param line 当前预览线对象。
     * @param isConstruction 是否按构造线样式显示。
     * @returns 可以复用时返回 true。
     */
    private canReuseCurvePreviewLine(line: THREE.Line, isConstruction: boolean): boolean {
        if (isConstruction) {
            return line.material instanceof THREE.LineDashedMaterial;
        }
        return line.material instanceof THREE.LineBasicMaterial
            && !(line.material instanceof THREE.LineDashedMaterial);
    }

    /**
     * 创建曲线预览材质。
     *
     * @param isConstruction 是否按构造线样式显示。
     * @returns 预览线材质。
     */
    private createCurvePreviewMaterial(isConstruction: boolean): THREE.LineBasicMaterial | THREE.LineDashedMaterial {
        return isConstruction
            ? new THREE.LineDashedMaterial({
                color: SPLINE_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.8,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: SPLINE_PREVIEW_COLOR,
                linewidth: 2,
                opacity: 0.8,
                transparent: true,
            });
    }

    /**
     * 替换预览线几何，并延迟释放上一帧仍可能被渲染器引用的旧 geometry。
     *
     * @param line 需要更新的预览线。
     * @param points 新的采样点。
     */
    private replacePreviewLineGeometry(line: THREE.Line, points: THREE.Vector3[]): void {
        const previousGeometry = line.geometry;
        line.geometry = new THREE.BufferGeometry().setFromPoints(points);
        scheduleDisposableResourceDisposal(previousGeometry);
        if (line.material instanceof THREE.LineDashedMaterial) {
            line.computeLineDistances();
        }
    }

    /**
     * 释放预览线对象。
     *
     * @param line 需要释放的预览线。
     */
    private disposePreviewLineObject(line: THREE.Line): void {
        if (line.parent === this.viewer.scene) {
            this.viewer.scene.remove(line);
        }
        scheduleDisposableResourceDisposal(line.geometry);
        if (line.material instanceof THREE.Material) {
            scheduleDisposableResourceDisposal(line.material);
        }
    }


    /**
     * 将控制折线预览渲染为构造草图线对象集合。
     * 说明：
     * 1. 每一段辅助线均使用 `SketchLineObject` 表达，保证与正式草图线拥有一致的类型语义；
     * 2. 预览材质仍沿用预览虚线视觉，但对象基础样式标记为构造线；
     * 3. 所有关联点与线都标记为辅助对象，避免进入业务选择事件。
     * @param controlPoints 控制点世界坐标列表。
     */
    private renderControlPolylinePreviewSegments(controlPoints: THREE.Vector3[]): void {
        if (controlPoints.length < 2) {
            this.disposeOverflowControlPolylinePreviewSegments(0);
            return;
        }
        const expectedLineCount = controlPoints.length - 1;
        while (this.sketchDrawingManager.previewConstructionLines.length < expectedLineCount) {
            const nextLineIndex = this.sketchDrawingManager.previewConstructionLines.length;
            const startPoint = this.createLineControlPoint(controlPoints[nextLineIndex], true);
            const endPoint = this.createLineControlPoint(controlPoints[nextLineIndex + 1], true);
            const previewMaterial = LineMaterialFactory.getPreviewDashedMaterial().clone();
            previewMaterial.opacity = 0.4;
            const previewLine = new SketchLineObject({
                startPoint,
                endPoint,
                material: previewMaterial,
                referencePlane: this.referencePlane,
                isConstruction: true,
                isAuxiliaryObject: true,
                isDrawingPreviewLine: true,
            });
            previewLine.computeLineDistances();
            this.sketchDrawingManager.previewConstructionLines.push(previewLine);
            this.viewer.addObject(previewLine);
        }
        this.disposeOverflowControlPolylinePreviewSegments(expectedLineCount);
        for (let index = 0; index < controlPoints.length - 1; index += 1) {
            const previewLine = this.sketchDrawingManager.previewConstructionLines[index] as SketchLineObject;
            previewLine.startPoint.syncPositionSilently(controlPoints[index]);
            previewLine.endPoint.syncPositionSilently(controlPoints[index + 1]);
            previewLine.syncEndpointsToGeometry();
            previewLine.computeLineDistances();
            if (previewLine.parent !== this.viewer.scene) {
                this.viewer.addObject(previewLine);
            }
        }
    }

    /**
     * 移除超过当前控制点数量的辅助折线预览段。
     *
     * @param expectedLineCount 当前应保留的线段数量。
     */
    private disposeOverflowControlPolylinePreviewSegments(expectedLineCount: number): void {
        while (this.sketchDrawingManager.previewConstructionLines.length > expectedLineCount) {
            const removedLine = this.sketchDrawingManager.previewConstructionLines.pop();
            if (!removedLine) {
                continue;
            }
            if (removedLine.parent === this.viewer.scene) {
                this.viewer.scene.remove(removedLine);
            }
            scheduleDisposableResourceDisposal(removedLine.geometry);
            if (removedLine.material instanceof THREE.Material) {
                scheduleDisposableResourceDisposal(removedLine.material);
            }
        }
    }


    /**
     * 同步样式样条绘制过程中的控制点预览对象。
     * @param controlPoints 当前应显示的控制点世界坐标列表。
     */
    private syncPreviewControlPointObjects(controlPoints: THREE.Vector3[]): void {
        const fixedControlPointCount = Math.max(controlPoints.length - 1, 0);

        while (this.previewControlPointObjects.length < fixedControlPointCount) {
            const previewPoint = this.createLineControlPoint(
                controlPoints[this.previewControlPointObjects.length],
                true,
            );

            // 样式样条控制点预览仅用于辅助显示，不参与业务点选事件。
            previewPoint.isAuxiliaryObject = true;

            previewPoint.isDrawingPreviewPoint = true;
            this.previewControlPointObjects.push(previewPoint);
        }

        this.previewControlPointObjects.forEach((previewPoint, index) => {
            if (index < fixedControlPointCount) {
                if (previewPoint.parent !== this.viewer.scene) {
                    this.viewer.addObject(previewPoint);
                }
                previewPoint.visible = true;
                previewPoint.syncPositionSilently(controlPoints[index]);
                return;
            }
            if (previewPoint.parent === this.viewer.scene) {
                this.viewer.scene.remove(previewPoint);
            }
            previewPoint.visible = false;
        });

        const movingControlPoint = controlPoints.at(-1) ?? null;
        if (!movingControlPoint) {
            if (this.previewMovingControlPointObject) {
                if (this.previewMovingControlPointObject.parent === this.viewer.scene) {
                    this.viewer.scene.remove(this.previewMovingControlPointObject);
                }
                this.previewMovingControlPointObject.visible = false;
            }
            return;
        }

        if (!this.previewMovingControlPointObject) {
            this.previewMovingControlPointObject = this.createLineControlPoint(movingControlPoint, true);

            // 跟随鼠标的临时控制点属于辅助对象，只承担预览职责。
            this.previewMovingControlPointObject.isAuxiliaryObject = true;

            this.previewMovingControlPointObject.isDrawingPreviewPoint = true;
        }
        if (this.previewMovingControlPointObject.parent !== this.viewer.scene) {
            this.viewer.addObject(this.previewMovingControlPointObject);
        }
        this.previewMovingControlPointObject.visible = true;
        this.previewMovingControlPointObject.syncPositionSilently(movingControlPoint);
    }

    /**
     * 销毁当前样式样条控制点预览对象。
     */
    private disposePreviewControlPoints(): void {
        if (!this.viewer.scene) {
            this.previewControlPointObjects.forEach((previewPoint) => {
                previewPoint.visible = false;
            });
            if (this.previewMovingControlPointObject) {
                this.previewMovingControlPointObject.visible = false;
            }
            return;
        }

        this.previewControlPointObjects.forEach((previewPoint) => {
            if (previewPoint.parent === this.viewer.scene) {
                this.viewer.scene.remove(previewPoint);
            }
            previewPoint.visible = false;
        });

        if (this.previewMovingControlPointObject) {
            if (this.previewMovingControlPointObject.parent === this.viewer.scene) {
                this.viewer.scene.remove(this.previewMovingControlPointObject);
            }
            this.previewMovingControlPointObject.visible = false;
        }
    }

    /**
     * 完整销毁样式样条预览。
     */
    private destroyStyleSplinePreviewCompletely(): void {
        super.destroyPreview();
        this.disposePreviewControlPoints();
    }

    /**
     * 获取当前绘制流程允许参与点捕捉的临时首点。
     * 说明：
     * 1. 仅在已经具备闭合条件时开放首点捕捉；
     * 2. 其它控制点继续保持为纯预览对象，不参与点捕捉；
     * 3. 返回对象来自预览控制点，不进入正式草图组集合。
     * @returns 当前允许参与捕捉的首点预览对象；不存在时返回 null。
     */
    public getTransientSnappableStartPoint(): SketchPointObject | null {
        if (!this.canSnapToClosureStart()) {
            return null;
        }
        return this.previewControlPointObjects[0] ?? null;
    }

    /**
     * 解析样式样条绘制点。
     * @param payload 鼠标按下载荷。
     * @returns 解析后的绘制点。
     */
    private resolveMultiPointDrawingPoint(payload: SketchDrawingMouseDownPayload): THREE.Vector3 | null {
        const point = payload.snappedPoint ?? this.getDrawingPoint(payload.event);
        if (!point) {
            return null;
        }


        // 只有明确捕捉到样式样条首点预览对象时才替换为链路起点，避免普通第三点被距离容差误判为闭合。
        if (this.isSnappedToClosureStart(payload)) {
            const chainStartPoint = this.resolveChainStartPointForClosure();
            if (chainStartPoint) {
                return chainStartPoint.clone();
            }
        }


        return point.clone();
    }


    /**
     * 判断当前点击是否明确吸附到样式样条链路起点。
     * @param payload 鼠标按下载荷。
     * @returns 是否命中样式样条首点预览对象。
     */
    private isSnappedToClosureStart(payload: SketchDrawingMouseDownPayload): boolean {
        const transientStartPoint = this.getTransientSnappableStartPoint();

        // 以捕捉目标对象身份作为闭合意图，避免仅凭坐标距离导致特征面第三次点击被误闭合。
        return transientStartPoint !== null && payload.snappedTargetPoint === transientStartPoint;
    }


    /**
     * 处理样式样条多点提交与闭合。
     * @param payload 鼠标按下载荷。
     * @returns 是否已提交。
     */
    private handleMultiPointCommit(payload: SketchDrawingMouseDownPayload): boolean {
        const chainStartPoint = this.resolveChainStartPointForClosure();


        // 闭合到链路起点：仅当当前点击明确吸附到样式样条首点预览对象时闭合。
        if (this.drawingPoints.length >= 2 && chainStartPoint && this.isSnappedToClosureStart(payload)) {
            this.drawingPoints.push(chainStartPoint.clone());
            this.scheduleFinishMultiPointDrawing();
            return true;
        }


        // 双击检测：必须来自浏览器原生双击计数，避免第三次普通点击因时间间隔短被误判结束。
        // 闭合完成后的第二击（isFinishingMultiPointDrawing 已置位）不得再次进入双击完成流程。
        if (payload.event.detail >= 2 && this.drawingPoints.length >= 2 && !this.isFinishingMultiPointDrawing) {
            //------ 双击控制点收口到预览模型 开始------
            // 浏览器原生双击由两次 mousedown 组成：第一击已把双击位置作为控制点 push 进 drawingPoints。
            // 这里把该临时落点替换为预览模型最后控制点（previewMovingControlPointObject 位置），
            // 保证最终生成曲线与预览模型一致：正常流下两位置重合（pop+push 无变化，不丢点），
            // 预览冻结/渲染延迟时收口到最后一次合法预览曲线。
            const previewLastControlPoint = this.previewMovingControlPointObject;
            if (previewLastControlPoint) {
                // 双击第一击已把末点坐标与吸附目标 push 进列表，位置回贴只替换坐标；
                // 必须保留原吸附目标，否则末点退化为新建点、与已有端点拓扑断开。
                const lastTarget = this.drawingPointTargets[this.drawingPointTargets.length - 1] ?? null;
                this.drawingPoints.pop();
                this.drawingPointTargets.pop();
                this.drawingPoints.push(previewLastControlPoint.getPosition().clone());
                this.drawingPointTargets.push(lastTarget);
            }
            this.scheduleFinishMultiPointDrawing();
            return true;
        }


        this.sketchDrawingManager.setLastLineClickTimestamp(Date.now());
        return false;
    }

    /**
     * 判断曲线起终点是否封闭。
     * @param start 起点。
     * @param end 终点。
     * @param epsilon 距离容差。
     * @returns 是否封闭。
     */
    private isClosedCurve(
        start: THREE.Vector3,
        end: THREE.Vector3,
        epsilon = SKETCH_GEOMETRY_TOLERANCE_CONFIG.closedCurvePosition,
    ): boolean {
        return start.distanceTo(end) < epsilon;
    }

    /**
     * 完成样式样条多点绘图。
     * @param options 完成选项。
     */
    public async finishMultiPointDrawing(options?: { stopDrawing?: boolean }): Promise<void> {
        if (this.isFinishingMultiPointDrawing) {
            return;
        }
        if (this.drawingPoints.length < 2) {
            return;
        }
        this.isFinishingMultiPointDrawing = true;
        try {
            await this.sketchDrawingManager.executeActiveSketchRuntimeHistorySession({
                label: `草图绘制：${SketchDrawingTool.StyleSpline}`,
                commandType: `sketch.runtime.draw.${SketchDrawingTool.StyleSpline}`,
                work: async () => {
                    await this.finishMultiPointDrawingCore(options);
                },
            });
        } finally {
            this.isFinishingMultiPointDrawing = false;
        }
    }

    /**
     * 执行样式样条实体创建与绘制状态清理。
     *
     * 说明：样式样条是不定点工具，事务必须放在完成入口，而不是每次点击落点时捕获整组草图。
     *
     * @param options 完成选项。
     */
    private async finishMultiPointDrawingCore(options?: { stopDrawing?: boolean }): Promise<void> {
        this.sketchDrawingManager.invalidatePreviewVersion();
        await this.createStyleSplineEntityByHandler();
        this.destroyStyleSplinePreviewCompletely();
        const stopDrawing = options?.stopDrawing ?? false;
        if (stopDrawing) {
            this.sketchDrawingManager.stopDrawing();
            return;
        }
        this.drawingPoints = [];
        // 绘制点清空时同步清空捕捉目标列表，避免残留引用影响下一次绘制。
        this.drawingPointTargets = [];
        this.sketchDrawingManager.setPreviewPoint(null);
        this.sketchDrawingManager.setLastLineClickTimestamp(0);
        this.viewer.lastClickTime = 0;
    }

    /**
     * 调度样式样条完成提交并集中处理异步失败。
     *
     * 说明：双击/闭合完成来自同步 mouseDown 事件，不能直接 await；
     * 显式捕获错误可以避免历史提交失败后静默破坏绘制状态。
     */
    private scheduleFinishMultiPointDrawing(): void {
        void this.finishMultiPointDrawing().catch((error) => {
            console.error('样式样条绘制提交失败', error);
            this.viewer.drawingNoticeCallback?.('样式样条绘制提交失败，请重试。', 'error');
        });
    }

    /**
     * 创建样式样条曲线实体。
     */
    private async createStyleSplineEntityByHandler(): Promise<void> {
        if (this.drawingPoints.length < 2) return;
        try {
            const controlPoints = this.drawingPoints.map((point) => ({x: point.x, y: point.y, z: point.z}));
            const mode = this.styleSplineOptions.mode;
            const isBezier = mode === StyleSplineMode.Bezier;
            const degree = mode === StyleSplineMode.BSpline3 ? 3
                : mode === StyleSplineMode.BSpline5 ? 5
                    : mode === StyleSplineMode.BSpline7 ? 7
                        : undefined;
            const isClosedInput = this.isClosedCurve(
                this.drawingPoints[0]!,
                this.drawingPoints[this.drawingPoints.length - 1]!,
            );
            const segments = resolveCurveSampleSegments(
                controlPoints,
                isClosedInput ? CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS : SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS,
            );
            const result = await this.viewer.workerManager.compute2DCurve({
                curveType: isBezier ? '2d_bezier' : '2d_bspline',
                params: {points: controlPoints, segments, degree},
            });
            const curvePoints = result.points.map((point) => new THREE.Vector3(point.x, point.y, point.z));

            // 当末次点击回捕首点时，样式样条应作为自闭合曲线进入对象层。
            const isSelfClosedSpline = this.isClosedCurve(curvePoints[0], curvePoints[curvePoints.length - 1]);

            const controlPointPositions = this.drawingPoints.map((point) => point.clone());
            // 解析与实体控制点位置一一对应的可复用捕捉目标，供控制点实体化时逐点复用。
            const controlPointTargets = this.resolveReusableControlPointTargets(
                controlPointPositions,
                this.drawingPointTargets,
            );
            const material = this.createSketchLineMaterial(this.styleSplineOptions.forConstruction);
            const pointObjects = this.createCurveControlPointObjects(controlPointPositions, controlPointTargets);
            const styleSplineObject = new SketchStyleSplineObject({
                startPoint: pointObjects.startPoint,
                endPoint: pointObjects.endPoint,
                controlPoints: pointObjects.controlPoints,
                curvePoints,
                material,
                referencePlane: this.referencePlane,
                isConstruction: this.styleSplineOptions.forConstruction,
                curveMetadata: {
                    selectionKind: SketchBezierSelectionKind.StyleSpline,
                    curveKind: isBezier ? SketchBezierCurveKind.Bezier : SketchBezierCurveKind.BSpline,

                    closureKind: isSelfClosedSpline
                        ? SketchBezierCurveClosureKind.Closed
                        : SketchBezierCurveClosureKind.Open,

                    styleSplineMode: mode,
                    degree: isBezier ? Math.max(controlPointPositions.length - 1, 1) : (degree ?? 3),
                    weights: new Array(controlPointPositions.length).fill(1),
                    isRational: false,
                },
            });
            if (material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(material)) {
                styleSplineObject.computeLineDistances();
            }
            if (this.viewer.shadedSketchContoursEnabled && styleSplineObject.isClosedGeometry) {
                styleSplineObject.syncContour();
            }
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(styleSplineObject);
            pointObjects.allPoints.forEach((point) => {
                this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(point);
            });
            this.viewer.addSketchObject(styleSplineObject);
            this.sketchDrawingManager.notifySketchEntityCreated(styleSplineObject);
            if (!isSelfClosedSpline) {
                this.tryFinalizeMixedClosedCollection([styleSplineObject]);
            }
            const sketchGroupManager = this.viewer.sketchGroupManager;
            if (!sketchGroupManager) {
                throw new Error('草图组管理器未初始化，无法同步样式样条闭合后的轮廓显示状态。');
            }
            this.viewer.syncShadedContoursAfterSketchEntityCreated(sketchGroupManager.activeSketchGroup);
        } catch (error) {
            throw error;
        }
    }

    /**
     * 为当前曲线绘制流程创建控制点对象集合。
     * @param controlPointPositions 控制点位置列表。
     * @param controlPointTargets 与位置列表等长的可复用捕捉目标列表；对应项非空时直接复用该点对象，否则新建。
     * @returns 控制点对象集合。
     */
    private createCurveControlPointObjects(
        controlPointPositions: THREE.Vector3[],
        controlPointTargets: readonly (SketchPointObject | null)[],
    ): {
        startPoint: SketchPointObject;
        endPoint: SketchPointObject;
        controlPoints: SketchPointObject[];
        allPoints: SketchPointObject[];
    } {
        if (controlPointPositions.length < 2) {
            throw new Error('曲线控制点数量不足，无法创建控制点对象。');
        }

        // 逐个控制点尝试复用吸附命中的已有端点：命中则直接共享点对象，未命中才新建，确保端点对象共享以支持选择链。
        const allPoints: SketchPointObject[] = controlPointPositions.map((position, index) => {
            const reusablePoint = controlPointTargets[index];
            return reusablePoint ?? this.createLineControlPoint(position);
        });

        return {
            startPoint: allPoints[0]!,
            endPoint: allPoints[allPoints.length - 1]!,
            controlPoints: allPoints.slice(1, -1),
            allPoints,
        };
    }
}
